/** Projections over the document catalog. */

import type { MentionKind } from '../comments/types'
import { projectString } from '../project/scalars'
import { projectSessions } from '../session/reads'

/** One document as a sidebar sees it, projected out of the project catalog. */
export interface DocumentRef {
  /** The document's Automerge doc id (the catalog key). */
  id: string
  title: string
  path: string
  mime: string
  updatedAt: number
  /** RFC 0267: the app data type this document instantiates, if any. */
  dataType?: string
}
/**
 * Project a project doc's document catalog (RFC 0263), most-recently-updated
 * first. Accepts the raw doc snapshot; returns [] for a project with no
 * documents, so a sidebar renders empty rather than throwing. Reads the catalog
 * ONLY (no per-document hydration), the same contract the session list relies on.
 */
export function projectDocuments(projectDoc: unknown): DocumentRef[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const documents = (projectDoc as { documents?: unknown }).documents
  if (!documents || typeof documents !== 'object') return []

  const out: DocumentRef[] = []
  for (const [id, raw] of Object.entries(
    documents as Record<string, unknown>,
  )) {
    if (!raw || typeof raw !== 'object') continue
    const r = raw as {
      title?: unknown
      path?: unknown
      mime?: unknown
      updatedAt?: unknown
      dataType?: unknown
    }
    const dataType = projectString(r.dataType)
    out.push({
      id,
      title: String(r.title ?? r.path ?? id),
      path: String(r.path ?? ''),
      mime: String(r.mime ?? 'text/plain'),
      updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : 0,
      ...(dataType ? { dataType } : {}),
    })
  }
  return out.sort((a, b) => b.updatedAt - a.updatedAt)
}
/** The project's explicit folders (RFC 0357 S8), by registry id. `path` is
 *  read as a string whether it was stored as text or a scalar (S11). */
export function projectFolders(
  projectDoc: unknown,
): Array<{ id: string; path: string }> {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const folders = (projectDoc as { folders?: unknown }).folders
  if (!folders || typeof folders !== 'object') return []
  const out: Array<{ id: string; path: string }> = []
  for (const [id, raw] of Object.entries(folders as Record<string, unknown>)) {
    const path =
      raw && typeof raw === 'object'
        ? projectString((raw as { path?: unknown }).path)
        : undefined
    if (path) out.push({ id, path })
  }
  return out
}
/** A thing that can be named from where the caller is typing (RFC 0292 §3c). */
export interface Candidate {
  kind: MentionKind
  id: string
  /** What the person sees and matches against. */
  label: string
  /** Disambiguates two things with the same label. */
  hint?: string
}
/** How many candidates a picker gets by default. A dropdown is scanned, not
 *  paged: past about this many the person types another letter instead. */
const CANDIDATE_LIMIT = 8
/**
 * Everything nameable from a project, ranked for a prefix (RFC 0292 §3c).
 *
 * A PURE PROJECTION over the project document, like `projectThreads`, and that
 * is the whole design decision. The three surfaces that need an `@` picker
 * cannot share a component — a plain textarea in the side column, ProseMirror
 * over a text CRDT, and a sandboxed iframe that reaches the host only through
 * the bridge — but they can share this, so the same letters offer the same names
 * everywhere, and an agent's tool resolves a name the way a person's dropdown
 * does.
 *
 * Ranked prefix-first, then substring, then most-recently-updated, because the
 * thing you are naming is usually the thing you were just looking at. Matching
 * is case-insensitive and ignores the leading `@`, which people type by habit.
 *
 * A candidate is a SESSION or a DOCUMENT. Not a person: RFC 0292 keeps naming
 * separate from admitting somebody to a session, which is `appendParticipant`.
 */
export function projectCandidates(
  projectDoc: unknown,
  prefix: string,
  opts?: { kinds?: MentionKind[]; limit?: number },
): Candidate[] {
  const kinds = opts?.kinds ?? ['session', 'document', 'task']
  const limit = opts?.limit ?? CANDIDATE_LIMIT
  const q = prefix.replace(/^@/, '').trim().toLowerCase()

  const pool: Array<Candidate & { updatedAt: number }> = []
  if (kinds.includes('session')) {
    for (const s of projectSessions(projectDoc)) {
      pool.push({
        kind: 'session',
        id: s.id,
        label: s.title,
        hint: 'session',
        updatedAt: s.updatedAt,
      })
    }
  }
  if (kinds.includes('document')) {
    for (const d of projectDocuments(projectDoc)) {
      pool.push({
        kind: 'document',
        id: d.id,
        label: d.title,
        // The path, when it says something the title does not. Two documents
        // called "notes" in different folders are otherwise one row twice.
        ...(d.path && d.path !== d.title ? { hint: d.path } : {}),
        updatedAt: d.updatedAt,
      })
    }
  }
  if (kinds.includes('task')) {
    const tasks = (projectDoc as { tasks?: Record<string, unknown> } | null)
      ?.tasks
    for (const [id, raw] of Object.entries(tasks ?? {})) {
      const task = raw as { title?: unknown; updatedAt?: unknown }
      pool.push({
        kind: 'task',
        id,
        label: String(task.title ?? id),
        hint: 'task',
        updatedAt:
          projectString(task.updatedAt) !== undefined
            ? Date.parse(projectString(task.updatedAt) as string) || 0
            : Number(task.updatedAt) || 0,
      })
    }
  }

  const scored: Array<{ c: Candidate; rank: number; updatedAt: number }> = []
  for (const entry of pool) {
    const { updatedAt, ...c } = entry
    if (!c.label) continue
    if (!q) {
      scored.push({ c, rank: 2, updatedAt })
      continue
    }
    const haystack = `${c.label} ${c.hint ?? ''}`.toLowerCase()
    if (c.label.toLowerCase().startsWith(q))
      scored.push({ c, rank: 0, updatedAt })
    else if (haystack.includes(q)) scored.push({ c, rank: 1, updatedAt })
  }

  scored.sort((a, b) => a.rank - b.rank || b.updatedAt - a.updatedAt)
  return scored.slice(0, limit).map((s) => s.c)
}
/**
 * The instances of one app data type (RFC 0267): the document catalog filtered
 * to entries tagged with `dataType`. What `mod.type.list()` and the agent's
 * "list the reviews" resolve to, off the catalog alone (no hydration).
 */
export function projectDocumentsOfType(
  projectDoc: unknown,
  dataType: string,
): DocumentRef[] {
  return projectDocuments(projectDoc).filter((d) => d.dataType === dataType)
}
/** An app instance's structured state (RFC 0267), from its own doc snapshot.
 *  `{}` when absent, so a view renders an empty instance rather than throwing. */
export function readDocumentData(
  documentDoc: unknown,
): Record<string, unknown> {
  if (!documentDoc || typeof documentDoc !== 'object') return {}
  const d = (documentDoc as { data?: unknown }).data
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : {}
}
/** A document's content (RFC 0263), from its own doc snapshot. '' if absent. */
/**
 * THE canonical way to read a document's text. Do not read `.content` directly.
 *
 * A document's content is one of two storage types: a text CRDT for prose, or
 * an `ImmutableString` scalar for build output (RFC 0274). This function is the
 * only place that knows, and callers get a plain string either way.
 *
 * Six legacy readers demonstrate the cost of skipping it. Each tests
 * `typeof content === 'string'` and falls through to nothing on a scalar, so a
 * published document reads as EMPTY with no error anywhere; one concatenates it
 * and would write "[object Object]" into a session. They are all on branch-layer
 * and file-sync surfaces that artifacts never reach, which is luck rather than
 * design.
 */
export function readDocumentContent(documentDoc: unknown): string {
  if (!documentDoc || typeof documentDoc !== 'object') return ''
  const c = (documentDoc as { content?: unknown }).content
  if (typeof c === 'string') return c
  // An `ImmutableString` is an Automerge SCALAR, not a text CRDT, and a
  // published build artifact is stored as one (RFC 0274). It is an OBJECT, so
  // the string check above silently returned '' for it: every reader saw an
  // empty document and a published view would have rendered blank. Read it
  // here so the storage choice stays invisible to callers, which is the only
  // way one document type can hold both prose and artifacts.
  if (c && typeof c === 'object' && 'val' in (c as Record<string, unknown>)) {
    const val = (c as { val?: unknown }).val
    if (typeof val === 'string') return val
  }
  if (c && typeof (c as { toString?: unknown }).toString === 'function') {
    const s = String(c)
    // Guard against the default object stringification leaking through.
    if (s !== '[object Object]') return s
  }
  return ''
}

/**
 * A file document's bytes (a PDF, an image), or null for a text document.
 *
 * NOT copied: a pane reads this on every render, and a 20 MB copy per
 * presence beat is the cost of a file document, not of looking at one. A
 * caller that hands the bytes to a Blob or a worker copies them there.
 */
export function readDocumentBytes(documentDoc: unknown): Uint8Array | null {
  if (!documentDoc || typeof documentDoc !== 'object') return null
  const b = (documentDoc as { bytes?: unknown }).bytes
  if (!(b instanceof Uint8Array) || b.byteLength === 0) return null
  return b
}
